/*
 * Copyright (c) 2023 - 2026 Bolmate B.V.
 */
import {
    FetchMethod,
    FetchOptions,
    navigate,
    PDFElementParams,
    PdfFont,
    ResponseData,
    sendFetch as controlFetch
} from '@dvrd/dvr-controls';

import {createParamUrl, URLS} from './api';

import {
    LVBSize,
    NotificationInterval,
    NotificationType,
    OAuthType,
    PeppolState,
    SalesColumn,
    SubscriptionModuleName
} from './enums';

import {onTrialEnd} from './events';

import {handlePossibleConnectionBlock} from './connectionRecovery';
import {isPaywallExemptPath} from './constants';
import {overviewBaseUrl} from './overviewApi';

import {
    AutoUploadType,
    BolReviewSorting,
    BolReviewTableSorting,
    CancelType,
    Country,
    CustomerType,
    DataSplitMethod,
    EmailFlow,
    ExportNameAttr,
    FilenameBase,
    Fulfilment,
    InvoiceExportType,
    InvoiceFilter,
    InvoiceUploadRule,
    InvoiceUploadStatus,
    NullString,
    OrderDirection,
    OverviewPeriodRequest,
    OverviewRequest,
    PaymentFailureStatus,
    ProcessType,
    ProductReviewAnalyse,
    RequestOptions,
    ReturnProcessingResult,
    ReviewSalesPeriod,
    SalesDashboardOrder,
    SalesDashboardPeriod,
    SendCostType,
    StripeCustomer,
    SubscriptionStatus,
    SystemMessageType,
    UnlayerDesign,
    UserFilter,
    UserRight,
    UserRole,
    UserStatus,
    UserType,
    VATValue
} from './interfaces';

import {getMaintenanceLoggedIn, requireMaintenanceLogin, setMaintenanceMode} from './maintenanceUtil';

import {SalesFilter} from './salesDashboardUtil';

import {getCSRF} from './storageUtil';

import {ProductSelection, Rating, SalesDashboardFilterGroup, SearchRankingMode} from './types';

interface ID {
    id: string;
}

interface Callback {
    callback: (data: ResponseData, response: Response) => void;
    /** Fired on a REAL failure (network error / non-2xx) — `callback` is only
     *  invoked for 2xx responses, so awaiting a Promise resolved from
     *  `callback` alone hangs forever on failure. Wire this when the caller
     *  awaits the request. */
    errorCallback?: (response: Response) => void;
}

interface WithAbortController {
    abortController?: AbortController;
}

type AbortCallback = {onAbort: VoidFunction};

interface ErrorCallback {
    errorCallback?: (response: Response) => void;
}

interface Data {
    data: object;
}

interface WithData<T> {
    data: T;
}

interface WithPartialData<T> {
    data?: Partial<T>;
}

interface OptionalID {
    id?: string | null;
}

interface WithID {
    id: string;
}

interface WithPagination {
    limit: number;
    page: number;
}

interface WithAccountID {
    account_id: string;
}

interface WithEAN extends WithAccountID {
    ean: string;
}

interface WithFingerprint {
    fp: string;
}

/*
 ================================================
 SUBSCRIPTION PROTECTED REQUESTS
 ================================================
 */
// Subscription-failure handling is centralised in bolmateFetch (shows the paywall and swallows the
// response while the dialog is on screen). Kept as a thin passthrough for its existing call sites.
const subscriptionRequiredFetch = (options: FetchOptions) => bolmateFetch(options);

// On paywall-exempt pages (settings/admin) several gated requests can fail with `subscription-failure`
// at once. Track when the message was last surfaced so bolmateFetch shows it only once per burst
// instead of stacking a dialog per failed request.
let lastSubscriptionFailureShownAt = 0;
const SUBSCRIPTION_FAILURE_DEBOUNCE_MS = 3000;

// Mirrors TrialEndController's `canBlock` via the shared PAYWALL_EXEMPT_ROUTES: the paywall covers
// every page except the exempt routes (settings, admin, iDEAL + Bol-connection flows). Computed from
// the URL (not the rendered dialog) so it is correct synchronously — on the first gated request the
// `.trial-end-dialog` node hasn't mounted yet, so a DOM check would misfire.
const paywallWillBlock = () => !isPaywallExemptPath(window.location.pathname);

/*
 ================================================
 AUTH
 ================================================
 */
interface RegisterData extends WithFingerprint {
    email: string;
    password: string;
    firstname: string;
    lastname?: string;
    flex?: object;
    invitation_id: string | null;
}

interface LoginData extends WithFingerprint {
    email: string;
    password: string;
}

interface GoogleAuth extends WithFingerprint {
    token: string;
}

interface VerifyIP extends WithFingerprint {
    verification_id: string;
    token: string;
}

export const register = (params: Callback & ErrorCallback & WithData<RegisterData>) => {
    bolmateFetch({
        url: URLS.POST.REGISTER,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback,
        // Without this, a non-2xx register (e.g. a Cloudflare 403 when the app
        // has been idle) is swallowed by bolmateFetch and the caller's callback
        // never fires — leaving the register spinner stuck forever. Same fix as
        // login: forward so the controller can reset loading and show a retry.
        errorCallback: params.errorCallback
    });
};

export const login = (params: Callback & ErrorCallback & WithData<LoginData>) => {
    bolmateFetch({
        url: URLS.POST.LOGIN,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback,
        // Without this, a non-2xx login (e.g. a Cloudflare 403 when the app has
        // been idle) is swallowed by bolmateFetch and the caller's callback never
        // fires — leaving the login spinner stuck forever. Forward it so the
        // controller can reset loading and surface a retryable error.
        errorCallback: params.errorCallback,
        maintenanceAllowed: true
    });
};

export function verifyIP(params: WithData<VerifyIP> & Callback) {
    return bolmateFetch({
        url: URLS.POST.VERIFY_IP,
        method: FetchMethod.POST,
        ...params
    });
}

export const logout = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.LOGOUT,
        callback: (responseData: ResponseData, response: Response) => {
            params.callback(responseData, response);
        },
        // Forward errorCallback: `callback` fires only on 2xx, so a non-2xx /
        // network failure must reach the caller — otherwise the logout flow
        // leaves the global loader on forever.
        errorCallback: params.errorCallback
    });
};

export function googleLogin(params: WithData<GoogleAuth> & Callback & ErrorCallback) {
    return bolmateFetch({
        url: URLS.POST.GOOGLE_LOGIN,
        method: FetchMethod.POST,
        ...params
    });
}

export function googleRegister(params: WithData<GoogleAuth> & Callback & ErrorCallback) {
    return bolmateFetch({
        url: URLS.POST.GOOGLE_REGISTER,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 USER
 ================================================
 */
interface UpdatePassword {
    password: string | null;
    new_password: string;
    activation_key: string | null;
}

interface ResetPassword {
    email: string;
}

interface SetVatNumber {
    vat_number: string;
}

type WithEmail = {email: string};

type CheckEmailExists = WithEmail &
    WithAccountID & {
        check_account_access?: boolean;
    };

interface ReferAFriend {
    friend_email: string;
}

export const getUser = (params: OptionalID & Callback & ErrorCallback) => {
    bolmateFetch({
        url: URLS.GET.USER,
        callback: params.callback,
        errorCallback: params.errorCallback,
        maintenanceAllowed: true
    });
};

export const updateUser = (params: Data & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_USER,
        method: FetchMethod.PUT,
        data: params.data,
        callback: params.callback
    });
};

export const resetPassword = (params: WithData<ResetPassword> & Callback & ErrorCallback) => {
    bolmateFetch({
        url: URLS.POST.RESET_PASSWORD,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback,
        // Forward so a blocked/non-2xx request (e.g. a Cloudflare 403 after idle)
        // resets the caller's spinner instead of hanging. Same fix as login.
        errorCallback: params.errorCallback
    });
};

export const updatePassword = (params: WithData<UpdatePassword> & Callback & ErrorCallback) => {
    bolmateFetch({
        url: URLS.PUT.CHANGE_PASSWORD,
        method: FetchMethod.PUT,
        data: params.data,
        callback: params.callback,
        // Forward so a blocked/non-2xx request (e.g. a Cloudflare 403 after idle)
        // resets the caller's spinner instead of hanging. Same fix as login.
        errorCallback: params.errorCallback
    });
};

export const deleteUser = (params: ID & Callback) => {
    bolmateFetch({
        url: URLS.DELETE.DELETE_USER,
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export function setVatNumber(params: WithData<SetVatNumber> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SET_VAT_NUMBER,
        method: FetchMethod.POST,
        ...params
    });
}

export function unsubscribeMonthlyMail(params: ID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.UNSUBSCRIBE_MONTHLY_MAIL, {user_id: params.id}),
        callback: params.callback
    });
}

export function emailExists(params: WithData<CheckEmailExists> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EMAIL_EXISTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function userHasSubscription(params: Callback) {
    return bolmateFetch({url: URLS.GET.USER_HAS_SUBSCRIPTION, ...params});
}

export function referAFriend(params: WithData<ReferAFriend> & Callback) {
    return bolmateFetch({
        url: URLS.POST.REFER_A_FRIEND,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 2FA
 ================================================
 */
interface ValidateTwoFactor extends WithFingerprint {
    token: string;
    two_factor_id: string | null;
}

export const generate2FaToken = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.GENERATE_TWO_FACTOR,
        callback: params.callback
    });
};

export const validate2FaToken = (params: WithData<ValidateTwoFactor> & Callback) => {
    bolmateFetch({
        url: URLS.POST.VALIDATE_TWO_FACTOR,
        method: FetchMethod.POST,
        ...params
    });
};

export const disable2Fa = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.DISABLE_TWO_FACTOR,
        ...params
    });
};

/*
 ================================================
 PDF Template
 ================================================
 */
interface CreateTemplate {
    account_id: string;
    elements: PDFElementParams<any, any>[];
    main_font: PdfFont;
}

type UpdateTemplate = WithData<Partial<CreateTemplate> & {template_id: string; image?: File}>;

interface PreviewTemplate {
    account_id: string;
    data: (CreateTemplate & {image?: File}) | Record<string, any>;
}

interface CopyFromAccount extends WithAccountID {
    from_account_id: string;
}

export const createPDFTemplate = (params: WithData<CreateTemplate> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_TEMPLATE,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
};

export const previewTemplate = (params: PreviewTemplate & Callback) => {
    const data = createPdfTemplateData(params);
    bolmateFetch({
        url: createParamUrl(URLS.POST.PREVIEW_TEMPLATE, {account_id: params.account_id}),
        data,
        method: FetchMethod.POST,
        callback: params.callback,
        stringifyData: !(data instanceof FormData)
    });
};

export const updatePDFTemplate = (params: UpdateTemplate & Callback) => {
    const data = createPdfTemplateData(params);
    bolmateFetch({
        url: URLS.PUT.UPDATE_TEMPLATE,
        method: FetchMethod.PUT,
        data,
        callback: params.callback,
        stringifyData: !(data instanceof FormData)
    });
};

const createPdfTemplateData = (params: WithData<Partial<CreateTemplate> & {template_id?: string; image?: File}>) => {
    let data: FormData | Record<string, any> = params.data;
    if (params.data.image) {
        data = new FormData();
        data.set('image', params.data.image);
        for (const key of Object.keys(params.data)) {
            if (key === 'image') continue;
            // @ts-expect-error Complains about params not having a index signature, but key comes directly from params
            data.set(key, JSON.stringify(params.data[key]));
        }
    }
    return data;
};

export function copyPDFTemplate(params: WithData<CopyFromAccount> & Callback) {
    return bolmateFetch({
        url: URLS.POST.COPY_PDF_TEMPLATE,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 PDFElement
 ================================================
 */

/*
 ================================================
 Account
 ================================================
 */

interface CreateAccount {
    company_name: string;
    address?: string | null;
    zip_code?: string | null;
    city?: string | null;
    country?: string | null;
    coc_number: string | null;
    vat_number: string | null;
    email: string;
    reply_email?: string | null;
    client_id?: string | null;
    client_secret?: string | null;
    settings?: Record<string, any>;
    logo?: File | null;
    tel_number?: string | null;
    default_vat?: number;
    invoice_prefix?: string;
    invoice_start?: string;
    use_bol_text?: boolean;
    use_platform_text?: boolean;
    email_template?: string;
    email_subject?: string | null;
    email_bcc?: string | null;
    iban?: string | null;
    sender_name: string | null;
    send_canceled_invoices?: boolean;
    vat_numbers?: Record<string, string>;
    flex?: Record<string, any>;
    platform_label?: string | null;
    reverse_vat_countries?: string[] | null;
    auto_respond_old_orders: boolean;
    old_order_invoice_prefix: string;
    old_order_invoice_start: string;
}

interface UpdateAccount extends Partial<CreateAccount> {
    account_id: string;
    auto_send_invoices?: boolean;
    email_max_product_name_length?: number | null;
    filename_base?: FilenameBase;
    auto_respond_invoice_requests?: boolean;
    proactively_upload_invoices?: boolean;
    invoice_upload_rules?: Array<InvoiceUploadRule>;
    notify_missing_invoice?: boolean;
    notify_missing_invoice_mail?: string | null;
    include_ubl_attachment?: boolean;
    participate_kor?: boolean;
    allow_data_usage?: boolean;
}

export const createAccount = (params: WithData<CreateAccount> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_ACCOUNT,
        method: FetchMethod.POST,
        data: createAccountData(params),
        callback: params.callback,
        stringifyData: !(params.data.logo instanceof File)
    });
};

export const getAccount = (params: {account_id: string} & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.GET_ACCOUNT, {account_id: params.account_id}),
        callback: params.callback
    });
};

export const updateAccount = (params: WithData<UpdateAccount> & Callback) => {
    return subscriptionRequiredFetch({
        url: URLS.PUT.UPDATE_ACCOUNT,
        method: FetchMethod.PUT,
        data: createAccountData(params),
        callback: params.callback,
        stringifyData: !(params.data.logo instanceof File)
    });
};

const createAccountData = (params: WithData<CreateAccount | UpdateAccount>) => {
    let data;
    if (params.data.logo) {
        data = new FormData();
        for (const key of Object.keys(params.data)) {
            if (key !== 'logo') {
                let value = params.data[key as keyof CreateAccount];
                if (typeof value !== 'string') value = JSON.stringify(value);
                if (value) data.set(key, value as string);
            }
        }
        data.set('logo', params.data.logo);
    } else data = params.data;
    return data;
};

export const pingAccountStatus = (params: ID & Callback) => {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ACCOUNT_STATUS, {account_id: params.id}),
        // The overview status banner reads what this ping writes onto the
        // account, so it follows the same dev-only switch as the overview
        // sections: `?overviewApi=mock` answers it from the spec, and it is
        // undefined — the app-wide platform URL — everywhere else.
        baseUrl: overviewBaseUrl(),
        callback: params.callback
    });
};

export const deleteAccount = (params: ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_ACCOUNT, {account_id: params.id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

/*
 ================================================
 Attachment
 ================================================
 */

interface UploadAttachment {
    account_id: string;
    file: File;
}

interface GetAttachments {
    limit?: number;
    page?: number;
    search_value?: string;
    account_id: string;
}

interface FindAttachments {
    account_id: string;
    attachment_ids: string[];
}

interface DeleteAttachment {
    attachment_id: string;
    account_id: string;
}

export const uploadAttachment = (params: UploadAttachment & Callback) => {
    const data = new FormData();
    data.set('file', params.file);
    bolmateFetch({
        url: createParamUrl(URLS.POST.UPLOAD_ATTACHMENT, {account_id: params.account_id}),
        method: FetchMethod.POST,
        data,
        stringifyData: false,
        callback: params.callback
    });
};

export const getAttachments = (params: WithData<GetAttachments> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_ATTACHMENTS,
        method: FetchMethod.POST,
        ...params
    });
};

export const findAttachments = (params: WithData<FindAttachments> & Callback) => {
    bolmateFetch({
        url: URLS.POST.FIND_ATTACHMENTS,
        method: FetchMethod.POST,
        ...params
    });
};

export const deleteAttachment = (params: WithData<DeleteAttachment> & Callback) => {
    bolmateFetch({
        url: URLS.POST.DELETE_ATTACHMENT,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 Email Step
 ================================================
 */

interface WithEmailStepId {
    email_step_id: string;
}

export interface CreateEmailStep {
    account_id: string;
    label: string;
    flow: EmailFlow;
    days?: number | null;
    template_id?: string | null;
    product_selections: ProductSelection;
    attachment_ids?: string[] | null;
    with_invoice: boolean;
    active: boolean;
    customer_type: CustomerType;
    only_work_days: boolean;
    countries: string[] | null;
    fulfilment: Fulfilment;
}

export type UpdateEmailStep = Partial<CreateEmailStep> & WithAccountID & WithEmailStepId;

interface UpdateAllEmailSteps {
    account_id: string;
    email_steps: Array<UpdateEmailStep>;
}

interface EmailStepStats {
    email_step_id: string;
    split_method: DataSplitMethod;
    period_start?: string | null;
    period_end?: string | null;
    account_id: string;
}

interface EmailStepStatsTotal {
    email_step_id: string;
    account_id: string;
}

interface GetAdminEmailSteps {
    accountId: string;
    userId: string;
}

interface AdminToggle {
    toggled: boolean;
}

type GetEmailSteps = WithAccountID & {
    search: string | null;
};

type LinkEmailProducts = WithAccountID & {
    file: File;
};

export const emailStepsAreEnabled = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.EMAILS_ENABLED, {account_id: params.account_id}),
        // Feeds the overview status banner, so it follows the same dev-only
        // switch as the rest of that page: `?overviewApi=mock` answers it from
        // the spec, undefined — the app-wide platform URL — everywhere else.
        baseUrl: overviewBaseUrl(),
        callback: params.callback
    });
};

export const createEmailStep = (params: WithData<WithAccountID & CreateEmailStep> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_EMAIL_STEP,
        method: FetchMethod.POST,
        ...params
    });
};

export function getEmailStep(params: WithEmailStepId & WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_EMAIL_STEP, {
            email_step_id: params.email_step_id,
            account_id: params.account_id
        }),
        callback: params.callback
    });
}

export const getEmailSteps = (params: WithData<GetEmailSteps> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_EMAIL_STEPS,
        method: FetchMethod.POST,
        ...params
    });
};

export function getAdminEmailSteps(params: GetAdminEmailSteps & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ADMIN_EMAIL_STEPS, {user_id: params.userId, account_id: params.accountId}),
        callback: params.callback
    });
}

export const updateEmailStep = (params: WithData<UpdateEmailStep> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_EMAIL_STEP,
        method: FetchMethod.PUT,
        ...params
    });
};

export const updateAllEmailSteps = (params: WithData<UpdateAllEmailSteps> & Callback) => {
    bolmateFetch({
        url: URLS.PATCH.PATCH_EMAIL_STEPS,
        method: FetchMethod.PATCH,
        ...params
    });
};

export const deleteEmailStep = (params: WithAccountID & WithEmailStepId & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_EMAIL_STEP, {
            account_id: params.account_id,
            email_step_id: params.email_step_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export const getEmailStepStats = (params: WithData<EmailStepStats> & Callback) => {
    bolmateFetch({
        url: URLS.POST.EMAIL_STEP_STATS,
        method: FetchMethod.POST,
        ...params
    });
};

export const getEmailStepStatsTotal = (params: EmailStepStatsTotal & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.GET_EMAIL_STEP_STATS_TOTAL, {
            account_id: params.account_id,
            email_step_id: params.email_step_id
        }),
        method: FetchMethod.GET,
        callback: params.callback
    });
};

export function getGlobalEmailStats(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GLOBAL_EMAIL_STATS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function getGlobalEmailPeriodStats(params: WithData<Omit<EmailStepStats, 'email_step_id'>> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GLOBAL_EMAIL_PERIOD_STATS,
        method: FetchMethod.POST,
        ...params
    });
}

export function sendHistoryMails(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.SEND_HISTORY_EMAIL, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function copyEmailSteps(params: WithData<CopyFromAccount> & Callback) {
    return bolmateFetch({
        url: URLS.POST.COPY_EMAIL_STEPS,
        method: FetchMethod.POST,
        ...params
    });
}

export function toggleAllServices(params: WithData<AdminToggle> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.TOGGLE_ALL_SERVICES,
        method: FetchMethod.PUT,
        ...params
    });
}

export function toggleMaintenanceMode(params: WithData<AdminToggle> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.ADMIN_TOGGLE_MAINTENANCE,
        method: FetchMethod.PUT,
        ...params
    });
}

export function linkEmailProducts(params: LinkEmailProducts & Callback) {
    const data = new FormData();
    data.set('file', params.file);
    data.set('account_id', params.account_id);
    return bolmateFetch({
        url: URLS.POST.LINK_EMAIL_PRODUCTS,
        method: FetchMethod.POST,
        data,
        stringifyData: false,
        callback: params.callback
    });
}

export function getPlannedEmailsCount(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_PLANNED_EMAILS_COUNT, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function getHasPlannedEmails(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.HAS_PLANNED_EMAILS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function cancelAllEmails(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.CANCEL_ALL_EMAILS, {account_id: params.account_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function validateEmailSteps(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.VALIDATE_EMAIL_STEPS, {account_id: params.account_id}),
        callback: params.callback
    });
}

/*
 ================================================
 Email Templates
 ================================================
 */

interface CreateEmailTemplate {
    account_id: string;
    name: string;
    subject?: string;
    body?: string;
    with_test_invoice?: boolean;
    mail_json?: Record<string, any> | null;
    html_body?: string;
}

interface GetEmailTemplate {
    account_id: string;
    template_id: string;
}

interface GetEmailTemplates {
    limit?: number;
    page?: number;
    account_id: string;
}

interface UpdateEmailTemplate {
    account_id: string;
    template_id: string;
    name: string;
    sender: string;
    reply_to: string | null;
    bcc: string | null;
    subject: string;
    body: string;
    with_test_invoice?: boolean;
    mail_json?: Record<string, any> | null;
    html_body?: string;
}

interface TestEmailTemplate {
    account_id: string;
    email: string;
    reply_to: string | null;
    subject: string;
    body: string;
    sender_name: string | null;
    html_body?: string | null;
    email_step_id?: string | null;
}

export const createEmailTemplate = (params: WithData<CreateEmailTemplate> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_EMAIL_TEMPLATE,
        method: FetchMethod.POST,
        ...params
    });
};

export const getEmailTemplate = (params: WithData<GetEmailTemplate> & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.GET_EMAIL_TEMPLATE, {
            account_id: params.data.account_id,
            template_id: params.data.template_id
        }),
        method: FetchMethod.GET,
        callback: params.callback
    });
};

export const getEmailTemplates = (params: WithData<GetEmailTemplates> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_EMAIL_TEMPLATES,
        method: FetchMethod.POST,
        ...params
    });
};

export const updateEmailTemplate = (params: WithData<UpdateEmailTemplate> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_EMAIL_TEMPLATE,
        method: FetchMethod.PUT,
        ...params
    });
};

export const testEmailTemplate = (params: WithData<TestEmailTemplate> & Callback) => {
    bolmateFetch({
        url: URLS.POST.TEST_EMAIL_TEMPLATE,
        method: FetchMethod.POST,
        ...params
    });
};

export const deleteEmailTemplate = (params: WithAccountID & WithID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_EMAIL_TEMPLATE, {account_id: params.account_id, template_id: params.id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export function duplicateEmailTemplate(params: WithAccountID & WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.DUPLICATE_TEMPLATE, {account_id: params.account_id, template_id: params.id}),
        callback: params.callback
    });
}

/*
 ================================================
 Invoice
 ================================================
 */

interface WithInvoiceId {
    invoice_id: string;
}

interface GetInvoices {
    limit?: number;
    page?: number;
    // Keyset cursor for "load more": the (date, id) of the last row already
    // loaded. When set the backend continues strictly past it instead of using
    // an OFFSET. Both must be present to take effect.
    cursor_date?: string | null;
    cursor_id?: string | null;
    filters: InvoiceFilter;
    account_id: string;
}

interface GetInvoiceStats {
    account_id: string;
    period_start?: string;
    period_end?: string;
}

interface DownloadOrSendInvoice extends WithInvoiceId {
    account_id: string;
    noHistory?: true;
}

interface DownloadMultipleInvoices extends WithAccountID {
    invoice_ids: string[] | 'all';
}

interface SendMultipleInvoices extends DownloadMultipleInvoices {
    send_now?: boolean;
}

interface SendInvoiceConfigured extends WithInvoiceId {
    account_id: string;
    mail_text: string;
    mail_json: Record<string, any>;
    subject: string;
}

export type UpdateInvoice = DownloadOrSendInvoice & Partial<CreateInvoice>;

export interface CreateInvoice {
    account_id: string;
    date: string;
    reference: string | null;
    company: string;
    firstname: string;
    lastname: string;
    street: string;
    house_number: string;
    house_number_suffix: string | null;
    zip_code: string;
    city: string;
    email: string;
    vat_number: string | null;
    coc_number: string | null;
    items: {
        item_id: string;
        ean: string;
        title: string;
        quantity: number;
        price: number;
        reference: string;
        vat: VATValue;
    }[];
    country?: Country;
    invoice_num?: string;
}

interface ExportInvoiceCount {
    from_date: string;
    to_date: string;
    account_id: string;
    country?: string | null;
    reverse_charge_only?: boolean;
    customer_type?: CustomerType;
    fulfilment: Fulfilment | null;
}

interface ExportInvoices extends ExportInvoiceCount {
    export_type: InvoiceExportType;
    name_attr?: ExportNameAttr;
    invoice_field_names?: Array<string>;
    order_field_names?: Array<string>;
}

interface BulkSequence {
    account_id: string;
    invoice_ids: string[];
}

interface AssignInvoiceNumber extends WithInvoiceId {
    account_id: string;
    invoice_num: string;
    prefix: string | null;
}

export const getMinimalInvoices = (params: WithData<GetInvoices> & Callback & Partial<AbortCallback>) => {
    return bolmateFetch({
        url: URLS.POST.GET_MINIMAL_INVOICES,
        method: FetchMethod.POST,
        ...params
    });
};

// Lazy per-order enrichment for the orders-cards UI (returns, product preview,
// profit). Split out of getMinimalInvoices so the list load stays fast; called
// with the visible page's invoice ids after the list renders.
export const getOrderCardDetails = (
    params: {account_id: string; invoice_ids: string[]} & Callback & Partial<AbortCallback>
) => {
    return bolmateFetch({
        url: URLS.POST.ORDER_CARD_DETAILS,
        method: FetchMethod.POST,
        data: {account_id: params.account_id, invoice_ids: params.invoice_ids},
        callback: params.callback,
        onAbort: params.onAbort
    });
};

export const getInvoice = (params: WithInvoiceId & WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.GET_INVOICE, {invoice_id: params.invoice_id, account_id: params.account_id}),
        callback: params.callback
    });
};

export const getInvoiceStats = (params: WithData<GetInvoiceStats> & Callback & Partial<AbortCallback>) => {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.INVOICE_STATS, {account_id: params.data.account_id}),
        callback: params.callback,
        onAbort: params.onAbort
    });
};

export const downloadInvoice = (params: DownloadOrSendInvoice & Callback) => {
    let url = createParamUrl(URLS.GET.DOWNLOAD_INVOICE, {invoice_id: params.invoice_id, account_id: params.account_id});
    if (params.noHistory) url += '?no-history=1';
    subscriptionRequiredFetch({
        url,
        callback: params.callback
    });
};

export const downloadInvoices = (params: WithData<DownloadMultipleInvoices> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.POST.DOWNLOAD_INVOICES,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
};

export const sendInvoice = (params: DownloadOrSendInvoice & Callback) => {
    subscriptionRequiredFetch({
        url: createParamUrl(URLS.GET.SEND_INVOICE, {invoice_id: params.invoice_id, account_id: params.account_id}),
        callback: params.callback
    });
};

export function sendInvoiceConfigured(params: WithData<SendInvoiceConfigured> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.SEND_INVOICE_CONFIGURED,
        method: FetchMethod.POST,
        ...params
    });
}

export const sendInvoices = (params: WithData<SendMultipleInvoices> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.POST.SEND_INVOICES,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
};

export const updateInvoice = (params: WithData<UpdateInvoice> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.PUT.UPDATE_INVOICE,
        method: FetchMethod.PUT,
        ...params
    });
};

export const getExportInvoiceCount = (params: WithData<ExportInvoiceCount> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_INVOICE_EXPORT_COUNT,
        method: FetchMethod.POST,
        ...params
    });
};

export const exportInvoices = (params: WithData<ExportInvoices> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.POST.EXPORT_INVOICES,
        method: FetchMethod.POST,
        ...params
    });
};

export const getInvoiceHistory = (params: DownloadOrSendInvoice & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.INVOICE_HISTORY, {invoice_id: params.invoice_id, account_id: params.account_id}),
        callback: params.callback
    });
};

export const bulkSequence = (params: WithData<BulkSequence> & Callback) => {
    bolmateFetch({
        url: URLS.POST.BULK_SEQUENCE,
        method: FetchMethod.POST,
        ...params
    });
};

export const assignInvoiceNumber = (params: WithData<AssignInvoiceNumber> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.INVOICE_NUMBER,
        method: FetchMethod.PUT,
        ...params
    });
};

export function cancelInvoiceEmails(params: WithInvoiceId & WithAccountID & Callback) {
    bolmateFetch({
        url: createParamUrl(URLS.GET.CANCEL_EMAILS, {invoice_id: params.invoice_id, account_id: params.account_id}),
        callback: params.callback
    });
}

/*
 ================================================
 Credit invoice
 ================================================
 */

export type CreditInvoiceItem = {
    ean: string;
    item_id: string;
    order_item_id: string;
    title: string;
    quantity: number;
    price: number;
    reference: string;
};

interface CreateCreditInvoice {
    account_id: string;
    invoice_id: string;
    items: CreditInvoiceItem[];
    date?: string | null;
    coc_number?: string | null;
    vat_number?: string | null;
}

export type UpdateCreditInvoiceItem = {
    id: string;
    quantity: number;
    price: number;
};

interface UpdateCreditInvoice {
    account_id: string;
    credit_invoice_id: string;
    items: UpdateCreditInvoiceItem[];
    delete_item_ids?: string[];
    add_items?: CreditInvoiceItem[];
    coc_number?: string | null;
    vat_number?: string | null;
}

interface UpdateCreditSettings {
    account_id: string;
    auto_create_credit_invoices?: boolean;
    auto_send_credit_invoices?: boolean;
    credit_invoice_num_start?: string;
    credit_invoice_num_prefix?: string;
}

export const createCreditInvoice = (params: WithData<CreateCreditInvoice> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREDIT_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
};

export const updateCreditInvoice = (params: WithData<UpdateCreditInvoice> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.CREDIT_INVOICE,
        method: FetchMethod.PUT,
        ...params
    });
};

export const updateCreditSettings = (params: WithData<UpdateCreditSettings> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_CREDIT_SETTINGS,
        method: FetchMethod.PUT,
        ...params
    });
};

export const getCreditEmailStep = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.CREDIT_EMAIL_STEP, {account_id: params.account_id}),
        callback: params.callback
    });
};

/*
 ================================================
 Reverse charge invoice
 ================================================
 */

interface CreateReverseCharge {
    account_id: string;
    invoice_id: string;
    vat_number: string;
    country: string;
    skip_validation?: boolean;
}

export const createReverseCharge = (params: WithData<CreateReverseCharge> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_REVERSE_CHARGE,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 Connections
 ================================================
 */

interface TestConnection {
    name: string;
}

export const getConnections = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.CONNECTIONS,
        ...params
    });
};

export const testConnection = (params: TestConnection & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.TEST_CONNECTION, {name: params.name}),
        callback: params.callback
    });
};

/*
 ================================================
 Admin
 ================================================
 */
interface GetAdminUsers {
    limit: number;
    page: number;
    search: NullString;
    filter: UserFilter | null;
}

interface GetChurn {
    from_date: string | null;
    to_date: string | null;
    split_method: DataSplitMethod;
}

interface UpdateAdminUser {
    status: UserStatus;
    type: UserType;
    firstname: string;
    lastname: string;
    flex: object | null;
    settings: object | null;
    tel_number: string | null;
    address: string | null;
    zip_code: string | null;
    city: string | null;
    email: string;
    subscription_status: SubscriptionStatus;
    override_limitations: boolean;
    admin_flex: object | null;
}

interface MixedID {
    user_id: string;
    account_id: string;
}

interface ImportHistory {
    user_id: string;
    account_id: string;
    invoice_num_prefix: string;
    invoice_num_start: string;
    send_emails: boolean;
    import_till_date: string | null;
    start_page?: number;
}

interface EmailSequenceCount {
    user_id: string;
    account_id: string;
    from_date: string | null;
    to_date: string | null;
}

interface AdminEmailSequence extends EmailSequenceCount {
    email_step_ids: Array<string> | null;
}

export function getAdminUsers(params: WithPartialData<GetAdminUsers> & Callback): AbortController | null {
    return bolmateFetch({
        url: URLS.POST.GET_USERS,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback
    });
}

export function getChurn(params: WithData<GetChurn> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_CHURN,
        method: FetchMethod.POST,
        ...params
    });
}

export interface GetPlatformUsageParams {
    from_date?: string; // YYYY-MM-DD
    to_date?: string; // YYYY-MM-DD
    bucket?: 'auto' | 'day' | 'week' | 'month';
    metric?: 'total_events' | 'unique_users';
    event_name?: 'page_view' | 'action_success' | 'action_failed';
    top_n?: number;
    breakdown?: 'feature_group' | 'feature_key';
    feature_key?: string;
    feature_group?: string;
    user_search?: string; // Search by email, account name, phone, etc.
    // User type filters
    include_regular_users?: boolean;
    include_admins?: boolean;
    include_admin_as_user?: boolean;
}

export function getPlatformUsage(params: WithPartialData<GetPlatformUsageParams> & Callback & ErrorCallback) {
    return bolmateFetch({
        url: URLS.POST.GET_PLATFORM_USAGE,
        method: FetchMethod.POST,
        data: params.data ?? {},
        ...params
    });
}

export interface TrackEventData {
    event_name: 'page_view' | 'action_success' | 'action_failed';
    feature_key: string;
    route?: string;
    action_key?: string;
    meta?: Record<string, unknown>;
}

export function trackAnalyticsEvent(params: WithData<TrackEventData> & Partial<Callback>) {
    // Fire and forget - no callback needed for tracking
    return bolmateFetch({
        url: URLS.POST.TRACK_ANALYTICS_EVENT,
        method: FetchMethod.POST,
        data: params.data,
        callback: params.callback ?? (() => {}) // Silent by default
    });
}

export function runAnalyticsAggregation(params: Callback) {
    return bolmateFetch({
        url: URLS.POST.RUN_ANALYTICS_AGGREGATION,
        method: FetchMethod.POST,
        data: {},
        ...params
    });
}

export function getAdminAccounts(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ADMIN_ACCOUNTS, {user_id: params.id}),
        callback: params.callback
    });
}

export function getAdminSubscriptionStats(params: Callback) {
    return bolmateFetch({
        url: URLS.GET.SUBSCRIPTION_STATS,
        ...params
    });
}

export const updateAdminUser = (params: WithPartialData<UpdateAdminUser> & ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.PUT.UPDATE_ADMIN_USER, {user_id: params.id}),
        method: FetchMethod.PUT,
        data: params.data,
        callback: params.callback
    });
};

export const deleteAdminUser = (params: ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_ADMIN_USER, {user_id: params.id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export const updateAdminAccount = (params: WithData<UpdateAccount> & ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.PUT.UPDATE_ADMIN_ACCOUNT, {user_id: params.id}),
        method: FetchMethod.PUT,
        data: createAccountData(params),
        callback: params.callback,
        stringifyData: !(params.data.logo instanceof File)
    });
};

export const deleteAdminAccount = (params: MixedID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_ADMIN_ACCOUNT, {user_id: params.user_id, account_id: params.account_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

export const adminLogin = (params: ID & Callback) => {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ADMIN_LOGIN, {user_id: params.id}),
        callback: params.callback,
        // Forward errorCallback: `callback` fires only on 2xx, so a non-2xx /
        // network failure must reach the caller — otherwise the admin-switch
        // flow leaves the global loader on forever.
        errorCallback: params.errorCallback
    });
};

export function importHistory(params: WithData<ImportHistory> & Callback) {
    return bolmateFetch({
        url: URLS.POST.IMPORT_HISTORY,
        method: FetchMethod.POST,
        ...params
    });
}

export function adminEmailSequence(params: WithData<AdminEmailSequence> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADMIN_EMAIL_SEQUENCE,
        method: FetchMethod.POST,
        ...params
    });
}

export function getEmailSequenceCount(params: WithData<EmailSequenceCount> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_EMAIL_SEQUENCE_COUNT,
        method: FetchMethod.POST,
        ...params
    });
}

export function getAdminEmailInfo(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ADMIN_EMAIL_INFO, {id: params.id}),
        callback: params.callback
    });
}

export function adminGenerateVerification(params: WithData<WithUserID> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADMIN_GENERATE_VERIFICATION,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Threads
 ================================================
 */
interface GetThreads {
    limit: number;
    page: number;
}

export const getThreads = (params: GetThreads & Callback) => {
    const url: string = `${URLS.GET.THREADS}?limit=${params.limit}&page=${params.page}`;
    bolmateFetch({
        url,
        callback: params.callback
    });
};

export const stopThread = (params: ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.STOP_THREAD, {thread_id: params.id}),
        callback: params.callback
    });
};

export const finishThread = (params: ID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.FINISH_THREAD, {thread_id: params.id}),
        callback: params.callback
    });
};

/*
 ================================================
 System service
 ================================================
 */
interface ToggleService {
    service_name: string;
    enabled: boolean;
}

export const getSystemServices = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.SYSTEM_SERVICES,
        callback: params.callback
    });
};

export const toggleSystemService = (params: WithData<ToggleService> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.TOGGLE_SERVICE,
        method: FetchMethod.PUT,
        ...params
    });
};

/*
 ================================================
 Bol Offers
 ================================================
 */

interface ProductsImport extends WithAccountID {
    use_crawler?: boolean;
}

interface GetProducts extends WithAccountID {
    limit?: number;
    page?: number;
    search?: string;
    eans?: string[];
    sort_custom_vat?: boolean;
    with_images?: boolean;
    exclude_on_hold?: boolean;
    hidden_products?: boolean;
}

type GetProductSelection = WithAccountID &
    ProductSelection &
    WithPagination & {
        search?: string | null;
    };

interface GetProductImages extends WithAccountID {
    eans: Array<string>;
}

export function getProducts(params: WithData<GetProducts> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_BOL_PRODUCTS, {account_id: params.data.account_id}),
        data: params.data,
        method: FetchMethod.POST,
        callback: params.callback
    });
}

export function getProductSelection(params: WithData<GetProductSelection> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_BOL_PRODUCTS_SELECTION,
        method: FetchMethod.POST,
        ...params
    });
}

interface GetProductsV2 {
    account_ids: Array<string>;
    search: string | null;
    limit: number;
    last_product_ean: string | null;
    require_meta: boolean;
    load_meta: boolean;
    expand: Array<string>;
}

export function getProductsV2(params: WithData<GetProductsV2> & Callback & Partial<AbortCallback>) {
    return bolmateFetch({
        url: URLS.POST.GET_BOL_PRODUCTS_V2,
        method: FetchMethod.POST,
        ...params
    });
}

export function generateQrCode(params: WithAccountID & {product_id: string} & Callback) {
    return subscriptionRequiredFetch({
        url: createParamUrl(URLS.GET.QR_CODE_GENERATE, {
            account_id: params.account_id,
            product_id: params.product_id
        }),
        callback: params.callback,
        errorCallback: params.errorCallback
    });
}

export function generateAllQrCodes(params: WithAccountID & Callback) {
    return subscriptionRequiredFetch({
        url: createParamUrl(URLS.GET.QR_CODE_GENERATE_ALL, {account_id: params.account_id}),
        callback: params.callback,
        errorCallback: params.errorCallback
    });
}

export function getProductImages(params: WithData<GetProductImages> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_PRODUCT_IMAGES,
        method: FetchMethod.POST,
        ...params
    });
}

export function getOpenProduct(params: WithEAN & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_OPEN_PRODUCT, {account_id: params.account_id, ean: params.ean}),
        callback: params.callback
    });
}

export function getProductsCount(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.PRODUCTS_COUNT, {account_id: params.account_id}),
        callback: params.callback
    });
}

export const importProductsAsync = (params: WithData<ProductsImport> & Callback) => {
    subscriptionRequiredFetch({
        url: URLS.POST.IMPORT_PRODUCTS_ASYNC,
        method: FetchMethod.POST,
        ...params
    });
};

export const getNumImporting = (params: ProductsImport & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.IMPORTING_PRODUCTS, {account_id: params.account_id}),
        callback: params.callback
    });
};

export function getProductDimensions(params: WithEAN & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_PRODUCT_DIMENSIONS, {account_id: params.account_id, ean: params.ean}),
        callback: params.callback
    });
}

export function getBolProductByEan(params: WithEAN & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_PRODUCT_BY_EAN, {account_id: params.account_id, ean: params.ean}),
        callback: params.callback
    });
}

/*
 ================================================
 API Key
 ================================================
 */
interface WithAPIKeyID extends WithAccountID {
    api_key_id: string;
}

interface UpdateAPIKey extends WithAPIKeyID {
    flex: Record<string, any>;
}

export const getApiKeys = (params: {account_id: string} & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.API_KEYS, {account_id: params.account_id}),
        callback: params.callback
    });
};

export const activateApiKey = (params: WithData<WithAPIKeyID> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.ACTIVATE_API_KEY,
        method: FetchMethod.PUT,
        ...params
    });
};

export function deactivateApiKey(params: WithAPIKeyID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DEACTIVATE_API_KEY, {
            account_id: params.account_id,
            api_key_id: params.api_key_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function updateAPIKey(params: WithData<UpdateAPIKey> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_API_KEY,
        method: FetchMethod.PUT,
        ...params
    });
}

/*
 ================================================
 Subscription
 ================================================
 */

interface StripePortal extends WithAccountID {
    return_url?: string;
}

interface CreateCheckout extends WithAccountID {
    success_url: string;
    cancel_url: string;
    payment_method?: string;
}

interface SetCancelReason extends WithAccountID {
    reason: string | null;
    cancel_type: CancelType;
}

interface ApplyCoupon extends WithAccountID {
    coupon: string;
}

interface CreateSubscriptionExistingUser {
    address: string;
    zip_code: string;
    city: string;
    country: string;
}

interface WithTaxID extends WithAccountID {
    tax_id: string;
}

export function setupIntent(params: WithAccountID & Callback) {
    bolmateFetch({
        url: createParamUrl(URLS.GET.SETUP_INTENT, {account_id: params.account_id}),
        callback: params.callback
    });
}

export const getStripePortal = (params: WithData<StripePortal> & Callback) => {
    bolmateFetch({
        url: URLS.POST.STRIPE_PORTAL,
        method: FetchMethod.POST,
        ...params
    });
};

export const createCheckout = (params: WithData<CreateCheckout> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CHECKOUT,
        method: FetchMethod.POST,
        ...params
    });
};

export const getSubscriptionUsage = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.SUBSCRIPTION_USAGE, {account_id: params.account_id}),
        callback: params.callback
    });
};

export const getSubscription = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.SUBSCRIPTION, {account_id: params.account_id}),
        callback: params.callback
    });
};

export const setCancelReason = (params: WithData<SetCancelReason> & Callback) => {
    bolmateFetch({
        url: URLS.POST.SET_CANCEL_REASON,
        method: FetchMethod.POST,
        ...params
    });
};

export function applyCoupon(params: WithData<ApplyCoupon> & Callback) {
    return bolmateFetch({
        url: URLS.POST.APPLY_COUPON,
        method: FetchMethod.POST,
        ...params
    });
}

export function discountOnCancel(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.DISCOUNT_ON_CANCEL, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function createSubscriptionExistingUser(params: WithData<CreateSubscriptionExistingUser> & Callback) {
    return bolmateFetch({
        url: URLS.POST.CREATE_SUBSCRIPTION_EXISTING_USER,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSubscriptionCustomer(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_SUBSCRIPTION_CUSTOMER, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function updateSubscriptionCustomer(params: WithData<WithAccountID & StripeCustomer> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_CUSTOMER,
        method: FetchMethod.PUT,
        ...params
    });
}

export function getSubscriptionTaxIDs(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_SUBSCRIPTION_TAX_IDS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function addTaxID(params: WithData<WithTaxID> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADD_TAX_ID,
        method: FetchMethod.POST,
        ...params
    });
}

export function deleteTaxID(params: WithTaxID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_TAX_ID, {account_id: params.account_id, tax_id: params.tax_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Subscription Modules
 ================================================
 */
interface WithSubscriptionModuleName extends WithAccountID {
    name: SubscriptionModuleName;
}

interface AddSubscriptionModule extends WithSubscriptionModuleName {
    check_exists?: boolean;
}

export function getSubscriptionModules(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_SUBSCRIPTION_MODULES, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function hasActiveSubscriptionModule(params: WithSubscriptionModuleName & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.HAS_ACTIVE_SUBSCRIPTION_MODULE, {
            account_id: params.account_id,
            name: params.name
        }),
        callback: params.callback
    });
}

export function addSubscriptionModule(params: WithData<AddSubscriptionModule> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADD_SUBSCRIPTION_MODULE,
        method: FetchMethod.POST,
        ...params
    });
}

export function cancelSubscriptionModule(params: WithSubscriptionModuleName & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.CANCEL_SUBSCRIPTION_MODULE, {account_id: params.account_id, name: params.name}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Export process
 ================================================
 */
interface GetExportProcesses {
    account_id: string;
    export_types?: Array<InvoiceExportType>;
    period_start?: string;
    period_end?: string;
}

interface DownloadExportProcess {
    account_id: string;
    export_process_id: string;
}

export const getExportProcesses = (params: WithData<GetExportProcesses> & Callback) => {
    bolmateFetch({
        url: URLS.POST.EXPORT_PROCESSES,
        method: FetchMethod.POST,
        ...params
    });
};

export const downloadExportProcess = (params: WithData<DownloadExportProcess> & Callback) => {
    bolmateFetch({
        url: URLS.POST.EXPORT_PROCESS_DOWNLOAD,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 VAT
 ================================================
 */

interface UpdateDefaultVATs extends WithAccountID {
    default_vats: Record<string, VATValue>;
    apply_retroactive: boolean;
}

interface UpdateBolProduct extends WithAccountID {
    product_id: string;
    vats: Record<string, VATValue>;
    title: string | null;
    apply_retroactive: boolean;
    hidden_by_user: boolean;
}

interface ImportVats extends WithAccountID {
    file: File;
    create_non_existing?: boolean;
    apply_retroactive?: boolean;
}

export const getDefaultVats = (params: WithAccountID & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.GET.DEFAULT_VATS, {account_id: params.account_id}),
        callback: params.callback
    });
};

export function updateDefaultVATs(params: WithData<UpdateDefaultVATs> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_DEFAULT_VATS,
        method: FetchMethod.PUT,
        ...params
    });
}

export function updateBolProduct(params: WithData<UpdateBolProduct> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_BOL_PRODUCT,
        method: FetchMethod.PUT,
        ...params
    });
}

export function importVats(params: ImportVats & Callback) {
    const formData = new FormData();
    formData.set('file', params.file);
    formData.set('account_id', params.account_id);
    if (params.apply_retroactive) formData.set('apply_retroactive', 'true');
    if (params.create_non_existing) formData.set('create_non_existing', 'true');
    return bolmateFetch({
        url: URLS.POST.IMPORT_VATS,
        method: FetchMethod.POST,
        data: formData,
        stringifyData: false,
        callback: params.callback
    });
}

export function exportVATs(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.EXPORT_PRODUCTS, {account_id: params.account_id}),
        callback: params.callback
    });
}

/*
 ================================================
 SUBSCRIPTION STATS
 ================================================
 */

interface GetSubscriptionStats {
    page: number;
    limit: number;
    split_method: DataSplitMethod;
    from_date?: string;
    to_date?: string;
}

export const getSubscriptionStats = (params: WithData<GetSubscriptionStats> & Callback) => {
    bolmateFetch({
        url: URLS.POST.GET_SUBSCRIPTION_STATS,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 SYSTEM MESSAGES
 ================================================
 */

interface CreateSystemMessage {
    message: string;
    message_type: SystemMessageType;
    priority: number;
    active?: boolean;
}

interface SystemMessageId {
    message_id: string;
}

export const createSystemMessage = (params: WithData<CreateSystemMessage> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREATE_SYSTEM_MESSAGE,
        method: FetchMethod.POST,
        ...params
    });
};

export const getSystemMessages = (params: Callback) => {
    bolmateFetch({
        url: URLS.GET.GET_SYSTEM_MESSAGES,
        ...params
    });
};

export const updateSystemMessage = (params: WithData<Partial<CreateSystemMessage> & SystemMessageId> & Callback) => {
    bolmateFetch({
        url: URLS.PUT.UPDATE_SYSTEM_MESSAGE,
        method: FetchMethod.PUT,
        ...params
    });
};

export const deleteSystemMessage = (params: SystemMessageId & Callback) => {
    bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_SYSTEM_MESSAGE, {message_id: params.message_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
};

/*
 ================================================
 BOL RETURNS
 ================================================
 */

interface GetBolReturns extends WithAccountID {
    page?: number;
    limit?: number;
    search?: string | null;
    status?: ReturnProcessingResult | null;
}

interface CreditReturn extends WithAccountID {
    return_id: string;
    rma_id: string;
    credit_all_items?: boolean;
}

interface GetReturnsCounts extends WithAccountID {
    period_start: string | null;
    period_end: string | null;
}

export const getBolReturns = (params: WithData<GetBolReturns> & Callback) => {
    return bolmateFetch({
        url: URLS.POST.GET_BOL_RETURNS,
        method: FetchMethod.POST,
        ...params
    });
};

export const getReturnCounts = (params: WithData<GetReturnsCounts> & Callback) => {
    return bolmateFetch({
        url: URLS.POST.RETURN_COUNTS,
        method: FetchMethod.POST,
        ...params
    });
};

export const creditReturnItem = (params: WithData<CreditReturn> & Callback) => {
    bolmateFetch({
        url: URLS.POST.CREDIT_RETURNS,
        method: FetchMethod.POST,
        ...params
    });
};

/*
 ================================================
 VAT DECLARATION
 ================================================
 */
interface WithPeriod {
    period_start: string;
    period_end: string;
}

interface GetVatDeclaration extends WithPeriod, WithAccountID {
    split_key: DataSplitMethod;
}

interface GetVatDeclarationWithOptions extends GetVatDeclaration {
    use_api?: boolean;
    recalculate?: boolean;
}

interface DownloadBolInvoice extends WithAccountID {
    file_type: 'pdf' | 'excel';
    invoice_id: string;
}

interface ExportVatDeclaration extends WithPeriod {
    account_id: string;
    export_type: InvoiceExportType;
}

export function getVatDeclaration(params: WithData<GetVatDeclarationWithOptions> & Callback) {
    return bolmateFetch({
        url: URLS.POST.VAT_DECLARATION,
        method: FetchMethod.POST,
        ...params
    });
}

export function downloadBolInvoice(params: DownloadBolInvoice & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.DOWNLOAD_BOL_INVOICE, {
            account_id: params.account_id,
            file_type: params.file_type,
            invoice_id: params.invoice_id
        }),
        callback: params.callback
    });
}

export function exportVatDeclaration(params: WithData<ExportVatDeclaration> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_VAT_DECLARATION,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 PROCESS STATUS
 ================================================
 */
interface ProcessStatusByType {
    process_type: ProcessType;
}

interface WithProcessStatusId extends WithAccountID {
    process_id: string;
}

interface AddMissingProducts extends WithProcessStatusId {
    deleteAfter?: boolean;
}

interface UpdateProcessStatus extends WithProcessStatusId {
    flex: Record<string, any>;
}

export function getProcessStatus(params: WithProcessStatusId & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.PROCESS_STATUS, {account_id: params.account_id, process_id: params.process_id}),
        callback: params.callback
    });
}

export function getProcessStatusByType(params: ProcessStatusByType & WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.PROCESS_STATUS_BY_TYPE, {
            account_id: params.account_id,
            process_type: params.process_type
        }),
        callback: params.callback
    });
}

export function getAllProcessStatusByType(params: ProcessStatusByType & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ALL_PROCESS_STATUS_BY_TYPE, {process_type: params.process_type}),
        callback: params.callback
    });
}

export function addMissingBolProducts(params: AddMissingProducts & Callback) {
    let url = createParamUrl(URLS.GET.ADD_MISSING_PRODUCTS, {
        account_id: params.account_id,
        process_id: params.process_id
    });
    if (params.deleteAfter) url += '?delete=1';
    return bolmateFetch({
        url,
        callback: params.callback
    });
}

export function deleteProcessStatus(params: WithProcessStatusId & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_PROCESS_STATUS, {
            account_id: params.account_id,
            process_id: params.process_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function updateProcessStatus(params: WithData<UpdateProcessStatus> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_PROCESS_STATUS,
        method: FetchMethod.PUT,
        ...params
    });
}

/*
 ================================================
 BETA
 ================================================
 */

interface SetBetaDecision {
    beta_type: string;
    decision: boolean | null;
}

export function setBetaDecision(params: WithData<SetBetaDecision> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SET_BETA_DECISION,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 BOL OAUTH
 ================================================
 */

interface AuthorizeBolOAuth extends WithAccountID {
    code: string;
    oauth_type: OAuthType;
}

export function getBolOAuthClient(params: Callback) {
    return bolmateFetch({url: URLS.GET.GET_BOL_OAUTH_CLIENT, ...params});
}

export function authorizeBolOAuth(params: WithData<AuthorizeBolOAuth> & Callback) {
    return bolmateFetch({
        url: URLS.POST.AUTHORIZE_BOL_OAUTH,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 2FA RESET
 ================================================
 */

interface ResetTwoFactor {
    token: string;
}

interface WithUserID {
    user_id: string;
}

export function resetTwoFactor(params: ResetTwoFactor & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.RESET_TWO_FACTOR, {token: params.token}),
        callback: params.callback
    });
}

export function generateTwoFactorReset(params: WithData<WithUserID> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GENERATE_TWO_FACTOR_RESET,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Invoice number
 ================================================
 */

interface GetLatestInvoiceNumber extends WithAccountID {
    invoice_num_prefix: string;
}

export function getLatestInvoiceNumber(params: WithData<GetLatestInvoiceNumber> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_LATEST_INVOICE_NUM,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Planned process
 ================================================
 */
interface PlanHistoryImport extends WithAccountID {
    invoice_num_prefix: string;
    invoice_num_start: string;
    is_default_num: boolean;
    import_orders: boolean;
    auto_upload: AutoUploadType;
    auto_upload_old: boolean;
    old_invoice_num_prefix: string;
    old_invoice_num_start: string;
    enable_notifications: Array<NotificationType>;
}

export function planHistoryImport(params: WithData<PlanHistoryImport> & Callback) {
    return bolmateFetch({
        url: URLS.POST.PLAN_HISTORY_IMPORT,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Invoice request
 ================================================
 */
export type GetInvoiceRequests = WithAccountID &
    WithPagination & {
        search: string | null;
        status: InvoiceUploadStatus | null;
        country: string | null;
    };

type UploadInvoice = WithAccountID &
    WithInvoiceId & {
        shipment_id?: string | null;
    };

interface UploadMultipleInvoices extends WithAccountID {
    upload_all_open: boolean;
    upload_invoice_ids: Array<string> | null;
}

interface ArchiveInvoiceRequest extends WithAccountID {
    order_id: string;
    shipment_id: string;
    customer_account_number: string | null;
    vat_number: string | null;
    kvk_number: string | null;
    request_date: string | null;
}

interface InvoiceRequestSetInvoice extends WithAccountID {
    invoice_request_id: number;
    invoice_id: string;
}

export function getInvoiceRequestStats(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.INVOICE_REQUEST_STATS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function getOpenInvoiceRequestCount(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.OPEN_REQUEST_COUNT, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function getInvoiceRequests(params: WithData<GetInvoiceRequests> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_INVOICE_REQUESTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function uploadInvoice(params: WithData<UploadInvoice> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.UPLOAD_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
}

export function uploadMultipleInvoices(params: WithData<UploadMultipleInvoices> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.UPLOAD_MULTIPLE_INVOICES,
        method: FetchMethod.POST,
        ...params
    });
}

export function invoiceRequestSetInvoice(params: WithData<InvoiceRequestSetInvoice> & Callback) {
    return bolmateFetch({
        url: URLS.POST.INVOICE_REQUEST_SET_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Product review request
 ================================================
 */

interface GetProductReviews extends WithAccountID {
    ean: string;
    account_id: string;
    sort_type: string;
    limit: string;
}

type GetProductReviewsFilter = WithAccountID &
    WithPagination & {
        filter_5_star: boolean;
        filter_4_star: boolean;
        filter_3_star: boolean;
        filter_2_star: boolean;
        filter_1_star: boolean;
        ean: string;
        sort_type: BolReviewSorting;
    };

interface GetReviewData extends WithAccountID {
    from_date: string | null;
    to_date: string | null;
}

type GetReviewProducts = GetReviewData &
    WithPagination & {
        search: string | null;
        sorting: BolReviewTableSorting;
        order_direction: OrderDirection;
        period: ReviewSalesPeriod;
    };

type GetReviewsFeed = WithAccountID &
    WithPagination & {
        filter_ratings: Array<number> | null;
    };

interface GetReviewProduct extends WithAccountID {
    ean: string;
}

interface AnalyseProductReviews extends WithEAN {
    analyse: ProductReviewAnalyse;
}

type GetSelectableReviewProducts = WithAccountID &
    WithPagination & {
        search: string | null;
    };

export function getProductReviewCounts(params: WithData<GetProductReviews> & Callback & WithAbortController) {
    return subscriptionRequiredFetch({
        url: URLS.POST.PRODUCT_REVIEW_COUNTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getReviewFeed(params: WithData<GetReviewsFeed> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.REVIEW_FEED,
        method: FetchMethod.POST,
        ...params
    });
}

export function getProductReviews(params: WithData<GetProductReviewsFilter> & Callback & WithAbortController) {
    return subscriptionRequiredFetch({
        url: URLS.POST.PRODUCT_REVIEWS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getReviewProducts(params: WithData<GetReviewProducts> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_REVIEW_PRODUCTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function archiveInvoiceRequest(params: WithData<ArchiveInvoiceRequest> & Callback) {
    return subscriptionRequiredFetch({
        url: URLS.POST.ARCHIVE_INVOICE_REQUEST,
        method: FetchMethod.POST,
        ...params
    });
}

export function getReviewProduct(params: GetReviewProduct & Callback & WithAbortController) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_REVIEW_PRODUCT, {account_id: params.account_id, ean: params.ean}),
        callback: params.callback
    });
}

export function getReviewTotals(params: WithData<GetReviewData> & Callback) {
    return bolmateFetch({
        url: URLS.GET.GET_REVIEW_TOTALS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getReviewChartData(params: WithData<GetReviewData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_REVIEW_CHART_DATA,
        method: FetchMethod.POST,
        ...params
    });
}

export function getAccountRatingCounts(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.ACCOUNT_RATING_COUNTS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function analyseProductReviews(params: WithData<AnalyseProductReviews> & Callback & WithAbortController) {
    return bolmateFetch({
        url: URLS.POST.ANALYSE_PRODUCT_REVIEWS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSelectableReviewProducts(params: WithData<GetSelectableReviewProducts> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SELECTABLE_REVIEW_PRODUCTS,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Ratings
 ================================================
 */
interface ExportRatings {
    account_ids: Array<string>;
    from_date: string | null;
    to_date: string | null;
    only_ratings: Array<Rating> | null;
    ean: string | null;
}

interface GetRatingExportProcesses extends WithAccountID {
    ean: string | null;
}

interface DownloadRatingExport extends WithAccountID {
    fileID: string;
}

interface DeleteRatingsExport extends WithAccountID {
    processID: string;
}

export function exportRatings(params: WithData<ExportRatings> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_RATINGS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getRatingExportProcesses(params: GetRatingExportProcesses & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_RATING_EXPORTS, {account_id: params.account_id, ean: params.ean}),
        callback: params.callback
    });
}

export function downloadRatingExport(params: DownloadRatingExport & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.DOWNLOAD_RATING_EXPORT, {account_id: params.account_id, file_id: params.fileID}),
        callback: params.callback
    });
}

export function deleteRatingsExport(params: DeleteRatingsExport & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_RATINGS_EXPORT, {
            account_id: params.account_id,
            process_id: params.processID
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Failed Payments
 ================================================
 */
interface GetFailedPayments extends WithPagination {
    status: PaymentFailureStatus | null;
    search: string | null;
}

interface WithFailedPaymentID {
    failed_payment_id: bigint;
}

interface FailedPaymentAction extends WithFailedPaymentID {
    user_id: string;
}

interface GenerateNewInvoice extends FailedPaymentAction {
    send_immediately?: boolean;
}

export function getFailedPayments(params: WithData<GetFailedPayments> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_FAILED_PAYMENTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function adminGenerateNewInvoice(params: WithData<GenerateNewInvoice> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADMIN_GENERATE_NEW_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
}

export function generateNewInvoice(params: WithData<GenerateNewInvoice> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GENERATE_NEW_INVOICE,
        method: FetchMethod.POST,
        ...params
    });
}

export function sendFailedPayment(params: FailedPaymentAction & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.SEND_FAILED_PAYMENT, {
            failed_payment_id: params.failed_payment_id.toString(),
            user_id: params.user_id
        }),
        callback: params.callback
    });
}

export function deleteFailedPayment(params: FailedPaymentAction & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_FAILED_PAYMENT, {
            failed_payment_id: params.failed_payment_id.toString(),
            user_id: params.user_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Sales dashboard
 ================================================
 */

interface GetSalesDashboardData extends WithAccountID {
    period: SalesDashboardPeriod;
    period_start?: string | null;
    period_end?: string | null;
    combine_account_ids: Array<string> | null;
}

type GetSalesDashboardProducts = GetSalesDashboardData &
    WithPagination & {
        search: string | null;
        order: SalesDashboardOrder;
        order_direction: OrderDirection;
        filters: SalesFilter;
    };

type ExportSalesDashboard = Omit<GetSalesDashboardProducts, 'limit' | 'page'> & {
    export_columns: Array<string>;
};

export function getSalesDashboardProducts(params: WithData<GetSalesDashboardProducts> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_SALES_DASHBOARD_PRODUCTS, {account_id: params.data.account_id}),
        method: FetchMethod.POST,
        ...params
    });
}

export function getSalesDashboardStats(params: WithData<GetSalesDashboardData> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_SALES_DASHBOARD_STATS, {account_id: params.data.account_id}),
        method: FetchMethod.POST,
        ...params
    });
}

export function getSalesDashboardChartData(params: WithData<GetSalesDashboardData> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_SALES_DASHBOARD_CHART_DATA, {account_id: params.data.account_id}),
        method: FetchMethod.POST,
        ...params
    });
}

export function exportSalesDashboard(params: WithData<ExportSalesDashboard> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_SALES_DASHBOARD,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Sales Dashboard V2
 ================================================
 */
interface GetSalesDashboardStatsV2 {
    account_ids: Array<string>;
    start_date: string;
    end_date: string;
    country_codes: Array<string> | null;
}

type GetSalesDashboardV2Products = WithPagination &
    GetSalesDashboardStatsV2 & {
        account_ids: Array<string>;
        search: string | null;
        start_date: string;
        end_date: string;
        order: SalesColumn | null;
        order_direction: OrderDirection;
        filters: Array<SalesDashboardFilterGroup> | null;
        country_codes: Array<string> | null;
    };

type ExportSalesDashboardV2 = Omit<GetSalesDashboardV2Products, 'limit' | 'page'> & {
    export_type: 'excel' | 'csv';
};

interface GetSalesDashboardProductV2 {
    account_ids: Array<string>;
    ean: string | null;
    start_date: string;
    end_date: string;
    group_method: DataSplitMethod;
    country_codes: Array<string> | null;
}

export function getSalesDashboardStatsV2(params: WithData<GetSalesDashboardStatsV2> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SALES_DASHBOARD_STATS_V2,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSalesDashboardV2Products(params: WithData<GetSalesDashboardV2Products> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SALES_DASHBOARD_V2_PRODUCTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function exportSalesDashboardV2(params: WithData<ExportSalesDashboardV2> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_SALES_DASHBOARD_V2,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSalesDashboardProductV2(params: WithData<GetSalesDashboardProductV2> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SALES_DASHBOARD_V2_PRODUCT,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Product costs
 ================================================
 */
interface SetProductCosts extends WithAccountID {
    ean: string;
    costs_id: string | null;
    start_date: string;
    fulfilment: Fulfilment;
    cost_price: number;
    fulfilment_cost: number;
    send_cost: number;
    send_cost_type: SendCostType;
    misc_costs_per_item: Record<string, number>;
    misc_costs_per_order: Record<string, number>;
}

interface GetProductCosts extends WithAccountID {
    ean: string;
    fulfilment: Fulfilment;
}

interface WithProductCostsID extends WithAccountID {
    costs_id: string;
}

interface ImportProductCosts extends WithAccountID {
    file: File;
    delete_current: boolean;
}

export function updateProductCosts(params: WithData<SetProductCosts> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SET_PRODUCT_COSTS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getProductCosts(params: GetProductCosts & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_PRODUCT_COSTS, {
            account_id: params.account_id,
            ean: params.ean,
            fulfilment: params.fulfilment
        }),
        callback: params.callback
    });
}

export function deleteProductCosts(params: WithProductCostsID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_PRODUCT_COSTS, {
            account_id: params.account_id,
            costs_id: params.costs_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function importProductCosts(params: ImportProductCosts & Callback) {
    const data = new FormData();
    data.set('file', params.file);
    data.set('account_id', params.account_id);
    data.set('delete_current', params.delete_current.toString());
    return bolmateFetch({
        url: URLS.POST.IMPORT_PRODUCT_COSTS,
        method: FetchMethod.POST,
        data,
        stringifyData: false,
        callback: params.callback
    });
}

export function exportProductCosts(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.EXPORT_PRODUCT_COSTS, {account_id: params.account_id}),
        callback: params.callback
    });
}

/*
 ================================================
 Product costs V2
 ================================================
 */
interface GetProductCostsV2 {
    account_ids: Array<string>;
    ean: string;
}

interface CreateProductCostsV2 extends WithEAN {
    start_date: string;
    cost_price: number | null;
    min_quantity: number;
    lvb_size: LVBSize | null;
    pick_costs: number | null;
    send_costs: number | null;
    return_label_costs: number | null;
    misc_return_costs: number | null;
    misc_return_description: string | null;
    misc_costs: number | null;
    misc_costs_description: string | null;
}

interface UpdateProductCostsV2 extends Partial<CreateProductCostsV2> {
    id: string;
}

type DeleteProductCostsV2 = WithID & WithAccountID;

export function getProductCostsV2(params: WithData<GetProductCostsV2> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_PRODUCT_COSTS_V2,
        method: FetchMethod.POST,
        ...params
    });
}

export function createProductCostsV2(params: WithData<CreateProductCostsV2> & Callback) {
    return bolmateFetch({
        url: URLS.POST.CREATE_PRODUCT_COSTS_V2,
        method: FetchMethod.POST,
        ...params
    });
}

export function updateProductCostsV2(params: WithData<UpdateProductCostsV2> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_PRODUCT_COSTS_V2,
        method: FetchMethod.PUT,
        ...params
    });
}

export function deleteProductCostsV2(params: DeleteProductCostsV2 & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_PRODUCT_COSTS_V2, {
            id: params.id,
            account_id: params.account_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 User Selection
 ================================================
 */

export interface UserSelectFilter extends WithPagination {
    created_before: string | null;
    created_after: string | null;
    subscription_started_before: string | null;
    subscription_started_after: string | null;
    subscription_status: 'active' | 'inactive' | null;
    include_trials: boolean;
    include_free: boolean;
    include_unpaid: boolean;
}

export function selectUsers(params: WithData<UserSelectFilter> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SELECT_USERS,
        method: FetchMethod.POST,
        ...params
    });
}

export function exportUsers(params: WithData<UserSelectFilter> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_USERS,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 User Estimate
 ================================================
 */

export function getActiveUserEstimate(params: Callback) {
    return bolmateFetch({
        url: URLS.GET.USERS_ESTIMATE,
        ...params
    });
}

/*
 ================================================
 Search term dashboard
 ================================================
 */
interface GetSearchTermResults extends WithAccountID {
    search_terms: string[];
    period: string;
    number_of_periods: number;
    with_comparison?: boolean;
}

interface GetSearchTermAIAnalysis extends WithAccountID {
    search_term: string;
}

export function getSearchVolumes(params: WithData<GetSearchTermResults> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SEARCH_VOLUMES,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSearchVolumesAIAnalysis(params: WithData<GetSearchTermAIAnalysis> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SEARCH_VOLUMES_AI,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Account user
 ================================================
 */
type GetAccountUsers = WithAccountID &
    WithPagination & {
        search: string | null;
    };

type UpdateAccountUser = WithUserID &
    WithAccountID & {
        user_role?: UserRole;
        rights?: Array<UserRight>;
    };

type RevokeAccountUser = WithUserID & WithAccountID;

type InviteUser = WithAccountID & {
    email: string;
    user_role: UserRole;
    rights: Array<UserRight>;
};

export function getAccountUsers(params: WithData<GetAccountUsers> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_ACCOUNT_USERS,
        method: FetchMethod.POST,
        ...params
    });
}

export function updateAccountUser(params: WithData<UpdateAccountUser> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_ACCOUNT_USER,
        method: FetchMethod.PUT,
        ...params
    });
}

export function revokeAccountUser(params: RevokeAccountUser & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.REVOKE_ACCOUNT_USER, {user_id: params.user_id, account_id: params.account_id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Account invitation
 ================================================
 */
type GetInvitations = WithAccountID &
    WithPagination & {
        search: string | null;
    };

type UpdateInvitation = Partial<InviteUser> & WithAccountID & WithInvitationID;

type WithInvitationID = {invitation_id: string};

type SendUserInvitation = WithAccountID & WithInvitationID;

export function checkInvitation(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.CHECK_INVITATION, {id: params.id}),
        callback: params.callback
    });
}

export function getInvitations(params: WithData<GetInvitations> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_INVITATIONS,
        method: FetchMethod.POST,
        ...params
    });
}

export function inviteAccountUser(params: WithData<InviteUser> & Callback) {
    return bolmateFetch({
        url: URLS.POST.INVITE_ACCOUNT_USER,
        method: FetchMethod.POST,
        ...params
    });
}

export function updateInvitation(params: WithData<UpdateInvitation> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_INVITATION,
        method: FetchMethod.PUT,
        ...params
    });
}

export function sendInvitation(params: WithData<SendUserInvitation> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SEND_INVITATION,
        method: FetchMethod.POST,
        ...params
    });
}

export function deleteInvitation(params: SendUserInvitation & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_INVITATION, {
            account_id: params.account_id,
            invitation_id: params.invitation_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

/*
 ================================================
 Default email templates
 ================================================
 */
interface CreateDefaultEmail {
    template_name: string;
    email_subject: string;
    json_template: UnlayerDesign;
    email_flows: Array<EmailFlow>;
    enabled?: boolean;
    preview_file?: File | null;
}

type UpdateDefaultEmail = WithID &
    Partial<CreateDefaultEmail> & {
        preview_file_id: string | null;
    };

interface GetDefaultEmails {
    email_flows?: Array<EmailFlow>;
}

type GetDefaultEmailsAdmin = WithPagination &
    GetDefaultEmails & {
        search?: string | null;
        enabled?: boolean | null;
        email_flow?: EmailFlow | null;
    };

type TestDefaultEmail = WithAccountID & {
    email_subject: string;
    email_content: string;
    recipient: string;
};

export function createDefaultEmail(params: WithData<CreateDefaultEmail> & Callback) {
    let data: FormData | Record<string, any>;
    let stringify = true;
    if (params.data.preview_file) {
        data = new FormData();
        const paramsData = params.data;
        data.set('template_name', paramsData.template_name);
        data.set('json_template', paramsData.json_template);
        data.set('email_flows', paramsData.email_flows);
        data.set('enabled', paramsData.enabled);
        data.set('preview_file', paramsData.preview_file);
        stringify = false;
    } else data = params.data;
    return bolmateFetch({
        url: URLS.POST.CREATE_DEFAULT_TEMPLATE,
        method: FetchMethod.POST,
        data,
        stringifyData: stringify,
        callback: params.callback
    });
}

export function getDefaultEmailsAdmin(params: WithData<GetDefaultEmailsAdmin> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADMIN_GET_DEFAULT_EMAIL_TEMPLATES,
        method: FetchMethod.POST,
        ...params
    });
}

export function getDefaultEmails(params: WithData<GetDefaultEmails> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_DEFAULT_EMAIL_TEMPLATES,
        method: FetchMethod.POST,
        ...params
    });
}

export function getDefaultEmail(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_DEFAULT_EMAIL_TEMPLATE, {id: params.id}),
        callback: params.callback
    });
}

export function updateDefaultEmail(params: WithData<UpdateDefaultEmail> & Callback) {
    let data: FormData | Record<string, any>;
    let stringify = true;
    if (params.data.preview_file) {
        data = new FormData();
        const paramsData = params.data;
        data.set('id', paramsData.id);
        data.set('template_name', paramsData.template_name);
        data.set('json_template', JSON.stringify(paramsData.json_template));
        data.set('email_flows', JSON.stringify(paramsData.email_flows));
        data.set('enabled', paramsData.enabled);
        data.set('preview_file', paramsData.preview_file);
        stringify = false;
    } else data = params.data;
    return bolmateFetch({
        url: URLS.PUT.UPDATE_DEFAULT_EMAIL_TEMPLATE,
        method: FetchMethod.PUT,
        data,
        stringifyData: stringify,
        callback: params.callback
    });
}

export function deleteDefaultEmail(params: WithID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_DEFAULT_EMAIL_TEMPLATE, {id: params.id}),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function testDefaultEmail(params: WithData<TestDefaultEmail> & Callback) {
    return bolmateFetch({
        url: URLS.POST.TEST_DEFAULT_EMAIL,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Notifications
 ================================================
 */
interface CreateNotification extends WithAccountID {
    notification_type: NotificationType;
    interval: NotificationInterval;
    email_addresses: Array<string>;
    product_selections: ProductSelection;
    flex: Record<string, any> | null;
}

interface UpdateNotification extends Partial<Omit<CreateNotification, 'notification_type'>> {
    notification_id: string;
}

interface DeleteNotification extends WithAccountID {
    notification_id: string;
}

interface SendTestNotification extends WithAccountID {
    notification_id: string;
    email_address: string;
}

interface NotificationFeedback {
    feedback: string;
}

export function createNotification(params: WithData<CreateNotification> & Callback) {
    return bolmateFetch({
        url: URLS.POST.CREATE_NOTIFICATION,
        method: FetchMethod.POST,
        ...params
    });
}

export function getNotifications(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_NOTIFICATIONS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function updateNotification(params: WithData<UpdateNotification> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_NOTIFICATION,
        method: FetchMethod.PUT,
        ...params
    });
}

export function deleteNotification(params: DeleteNotification & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.DELETE.DELETE_NOTIFICATION, {
            account_id: params.account_id,
            notification_id: params.notification_id
        }),
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export function sendTestNotification(params: WithData<SendTestNotification> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SEND_TEST_NOTIFICATION,
        method: FetchMethod.POST,
        ...params
    });
}

export function submitNotificationFeedback(params: WithData<NotificationFeedback> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SUBMIT_NOTIFICATION_FEEDBACK,
        method: FetchMethod.POST,
        ...params
    });
}

export function getNotificationAccounts(params: Callback) {
    return bolmateFetch({url: URLS.GET.GET_NOTIFICATION_ACCOUNTS, ...params});
}

/*
 ================================================
 Feedback
 ================================================
 */
interface SubmitFeedback extends WithAccountID {
    feedback: string;
    current_path: string;
}

export function submitFeedback(params: WithData<SubmitFeedback> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SUBMIT_FEEDBACK,
        method: FetchMethod.POST,
        ...params
    });
}

/*
 ================================================
 Peppol
 ================================================
 */
type GetPeppolInvoices = WithAccountID &
    WithPagination & {
        peppol_state: PeppolState | null;
        search: string | null;
    };

type SendPeppolInvoice = WithInvoiceId & WithAccountID;

interface TogglePeppolEnabled extends WithAccountID {
    enabled: boolean;
}

export function getPeppolInvoices(params: WithData<GetPeppolInvoices> & Callback) {
    return bolmateFetch({url: URLS.POST.GET_PEPPOL_INVOICES, method: FetchMethod.POST, ...params});
}

export function getPeppolInvoiceCounts(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_PEPPOL_INVOICE_COUNTS, {account_id: params.account_id}),
        callback: params.callback
    });
}

export function sendPeppolInvoice(params: WithData<SendPeppolInvoice> & Callback) {
    return bolmateFetch({url: URLS.POST.SEND_PEPPOL_INVOICE, method: FetchMethod.POST, ...params});
}

export function togglePeppolEnabled(params: WithData<TogglePeppolEnabled> & Callback) {
    return bolmateFetch({url: URLS.POST.TOGGLE_PEPPOL_ENABLED, method: FetchMethod.POST, ...params});
}

export function getPeppolEnabled(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_PEPPOL_ENABLED, {account_id: params.account_id}),
        callback: params.callback
    });
}

/*
 ================================================
 Analytics Settings
 ================================================
 */

export function getAnalyticsSettings(params: Callback) {
    return bolmateFetch({
        url: URLS.GET.GET_ANALYTICS_SETTINGS,
        callback: params.callback
    });
}

export interface ToggleAnalyticsCollectionData {
    enabled: boolean;
}

export function toggleAnalyticsCollection(params: WithData<ToggleAnalyticsCollectionData> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.TOGGLE_ANALYTICS_COLLECTION,
        method: FetchMethod.PUT,
        ...params
    });
}

export function clearAnalyticsData(params: Callback) {
    return bolmateFetch({
        url: URLS.DELETE.CLEAR_ANALYTICS_DATA,
        method: FetchMethod.DELETE,
        callback: params.callback
    });
}

export interface UpdateAnalyticsSettingsData {
    version: number;
    collection_enabled?: boolean;
    raw_events_retention_days?: number;
    user_day_retention_days?: number;
    daily_aggregates_retention_days?: number;
    aggregation_delay_minutes?: number;
    push_counter_retention_days?: number;
    push_stale_token_days?: number;
    push_unconfirmed_threshold?: number;
    web_push_cleanup_enabled?: boolean;
    web_push_unconfirmed_cleanup_enabled?: boolean;
    push_tray_limit?: number;
}

export function updateAnalyticsSettings(params: WithData<UpdateAnalyticsSettingsData> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.UPDATE_ANALYTICS_SETTINGS,
        method: FetchMethod.PUT,
        ...params
    });
}

/*
 ================================================
 Search Rankings
 ================================================
 */

interface GetSearchRankingProductsData extends WithAccountID {
    country_codes?: Array<'NL' | 'BE'>;
    ranking_mode?: SearchRankingMode;
    search?: string | null;
    position_filter?: string | null;
    sort_field?: string | null;
    sort_order?: OrderDirection;
    account_ids?: Array<string> | null;
}

type GetSearchRankingProducts = GetSearchRankingProductsData & WithPagination;

interface GetSearchRankingStatsData extends WithAccountID {
    country_codes?: Array<'NL' | 'BE'>;
    account_ids?: Array<string> | null;
    search?: string | null;
    position_filter?: string | null;
    sponsored_filter?: string | null;
}

interface GetProductKeywordRankingsData extends WithAccountID {
    ean: string;
    country_codes?: Array<'NL' | 'BE'>;
    include_archived?: boolean;
    ranking_mode: SearchRankingMode;
    account_ids: Array<string> | null;
}

interface GetKeywordTrendData extends WithAccountID {
    ean: string;
    keywords: Array<string>;
    country_codes?: Array<'NL' | 'BE'>;
    account_ids: Array<string> | null;
}

interface AddSearchRankingKeywordData extends WithAccountID {
    ean: string;
    keyword: string;
}

interface BulkImportKeywordsData extends WithAccountID {
    keywords: Record<string, Array<string>>;
}

interface ArchiveSearchRankingKeywordData extends WithAccountID {
    ean: string;
    keyword: string;
    is_archived: boolean;
    account_ids: Array<string> | null;
}

interface ArchiveSearchRankingCategory extends WithAccountID {
    category_id: string;
    archive: boolean;
    account_ids: Array<string> | null;
}

interface GetProductCategoryRankingsData extends WithAccountID {
    ean: string;
    country_codes?: Array<'NL' | 'BE'>;
    ranking_mode: SearchRankingMode;
    account_ids: Array<string> | null;
}

export function getSearchRankingProducts(params: WithData<GetSearchRankingProducts> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_SEARCH_RANKING_PRODUCTS, {account_id: params.data.account_id}),
        method: FetchMethod.POST,
        ...params
    });
}

export function getSearchRankingStats(params: WithData<GetSearchRankingStatsData> & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.POST.GET_SEARCH_RANKING_STATS, {account_id: params.data.account_id}),
        method: FetchMethod.POST,
        ...params
    });
}

export function getSearchRankingProductKeywords(params: WithData<GetProductKeywordRankingsData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SEARCH_RANKING_PRODUCT_KEYWORDS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSearchRankingKeywordsTrend(params: WithData<GetKeywordTrendData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SEARCH_RANKING_KEYWORD_TREND,
        method: FetchMethod.POST,
        ...params
    });
}

export function addSearchRankingKeyword(params: WithData<AddSearchRankingKeywordData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.ADD_SEARCH_RANKING_KEYWORD,
        method: FetchMethod.POST,
        ...params
    });
}

export function bulkImportKeywords(params: WithData<BulkImportKeywordsData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.BULK_IMPORT_KEYWORDS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSearchRankingProductCategories(params: WithData<GetProductCategoryRankingsData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SEARCH_RANKING_PRODUCT_CATEGORIES,
        method: FetchMethod.POST,
        ...params
    });
}

export function archiveSearchRankingKeyword(params: WithData<ArchiveSearchRankingKeywordData> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.ARCHIVE_SEARCH_RANKING_KEYWORD,
        method: FetchMethod.PUT,
        ...params
    });
}

export function archiveSearchRankingCategory(params: WithData<ArchiveSearchRankingCategory> & Callback) {
    return bolmateFetch({
        url: URLS.PUT.ARCHIVE_SEARCH_RANKINGS_CATEGORY,
        method: FetchMethod.PUT,
        ...params
    });
}

export interface SearchRankingFilters {
    ranking_mode?: SearchRankingMode | null;
    country_codes?: Array<'NL' | 'BE'> | null;
    account_ids?: Array<string> | null;
}

interface SaveSearchRankingColumnsData extends WithAccountID {
    columns: Array<string>;
    preset_name?: string | null;
    filters?: SearchRankingFilters | null;
}

export function saveSearchRankingColumns(params: WithData<SaveSearchRankingColumnsData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.SAVE_SEARCH_RANKING_COLUMNS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSearchRankingColumns(params: WithData<WithAccountID> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SEARCH_RANKING_COLUMNS,
        method: FetchMethod.POST,
        ...params
    });
}

interface GetBuyboxTrendData extends WithAccountID {
    ean: string;
    country_codes?: Array<'NL' | 'BE'>;
    account_ids: Array<string> | null;
}

export function getSearchRankingBuyboxTrend(params: WithData<GetBuyboxTrendData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SEARCH_RANKING_BUYBOX_TREND,
        method: FetchMethod.POST,
        ...params
    });
}

interface GetCategoriesTrendData extends WithAccountID {
    ean: string;
    country_codes?: Array<'NL' | 'BE'>;
    account_ids: Array<string> | null;
}

export function getSearchRankingCategoryTrend(params: WithData<GetCategoriesTrendData> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SEARCH_RANKING_CATEGORY_TREND,
        method: FetchMethod.POST,
        ...params
    });
}

interface GetSuggestedKeywords extends WithEAN {
    exclude_keywords: Array<string> | null;
}

export function exportSearchRankings(params: WithData<GetSearchRankingProducts> & Callback) {
    return bolmateFetch({
        url: URLS.POST.EXPORT_SEARCH_RANKINGS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getSuggestedKeywords(params: WithData<GetSuggestedKeywords> & Callback) {
    return bolmateFetch({
        url: URLS.POST.GET_SUGGESTED_KEYWORDS,
        method: FetchMethod.POST,
        ...params
    });
}

export function getManualKeywordsCount(params: WithAccountID & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.GET.GET_MANUAL_KEYWORDS_COUNT, {account_id: params.account_id}),
        callback: params.callback
    });
}

/*
 ================================================
 Util
 ================================================
 */

/*
 ================================================
 Device tokens / web push (Phase 5)
 ================================================
 */

export type DeviceTokenChannel = 'EMAIL' | 'WEB_PUSH' | 'FCM' | 'APNS';

export interface RegisterDeviceToken {
    account_id: string;
    channel: DeviceTokenChannel;
    endpoint: string;
    // The browser's PREVIOUS subscription endpoint, sent only when this
    // browser's subscription rotated (SW pushsubscriptionchange). The backend
    // reaps that exact stale row. Never sent to identify a device.
    previous_endpoint?: string;
    p256dh: string;
    auth: string;
    user_agent?: string;
    platform?: string;
    native_token?: string | null;
}

interface DeleteDeviceToken {
    token_id: string;
}

interface UpdateNotificationChannels {
    notification_id: string;
    // Required: the backend validator (UpdateNotificationChannels) extends
    // WithAccountID and the handler is @account_validated, so the channel
    // update is scoped to the notification's sales account. Omitting it makes
    // the request fail with {"type":"validation_error","errors":["Field required"]}.
    account_id: string;
    channels: Array<DeviceTokenChannel>;
    // Per-notification push-device targeting (device-token ids). OMITTED →
    // the backend leaves the stored targeting untouched; [] → clears it (and
    // strips WEB_PUSH); a non-empty list replaces it. Absent server-side =
    // "all devices" (legacy).
    push_device_ids?: Array<string>;
}

export function registerDeviceToken(params: WithData<RegisterDeviceToken> & Callback) {
    return bolmateFetch({
        url: URLS.POST.REGISTER_DEVICE_TOKEN,
        method: FetchMethod.POST,
        ...params
    });
}

export function unregisterDeviceToken(params: WithData<{account_id: string; endpoint: string}> & Callback) {
    // A browser cleaning up its own subscription (removal by endpoint). Shares the
    // single /device-token/remove endpoint with deleteDeviceToken (removal by id).
    return bolmateFetch({
        url: URLS.POST.REMOVE_DEVICE_TOKEN,
        method: FetchMethod.POST,
        ...params
    });
}

export function deleteDeviceToken(params: DeleteDeviceToken & {account_id: string} & Callback) {
    // Device panel removing a listed device by id — same /device-token/remove
    // endpoint as unregisterDeviceToken, only the identifier in the body differs.
    return bolmateFetch({
        url: URLS.POST.REMOVE_DEVICE_TOKEN,
        method: FetchMethod.POST,
        data: {account_id: params.account_id, token_id: params.token_id},
        callback: params.callback,
        // callback fires only on 2xx; DeviceList's orphan cleanup awaits this, so
        // forward errorCallback too (mirrors renameDeviceToken).
        errorCallback: params.errorCallback
    });
}

export function renameDeviceToken(
    params: {token_id: string; account_id: string; custom_name: string | null} & Callback
) {
    // token_id in the path; account_id + custom_name in the JSON body. A blank
    // custom_name clears the nickname (the row falls back to its auto label).
    return bolmateFetch({
        url: createParamUrl(URLS.PUT.UPDATE_DEVICE_TOKEN, {token_id: params.token_id}),
        method: FetchMethod.PUT,
        data: {account_id: params.account_id, custom_name: params.custom_name},
        callback: params.callback,
        // Forward errorCallback: `callback` fires only on 2xx, so a non-2xx /
        // network failure must reach the caller's errorCallback (else the caller
        // can never clear its loading state). Mirrors registerDeviceToken.
        errorCallback: params.errorCallback
    });
}

export function getMailRedirectInfo(params: Callback) {
    // Whether outgoing mails are rerouted to the config.ini test address
    // (every non-prod environment) — the admin panel annotates the recipient
    // list with it.
    return bolmateFetch({
        url: URLS.GET.MAIL_REDIRECT_INFO,
        callback: params.callback,
        errorCallback: params.errorCallback
    });
}

export function listDeviceTokens(params: {account_id: string} & Callback) {
    // account_id appended as query string — LIST_DEVICE_TOKENS URL has no
    // path placeholder, so createParamUrl would be a no-op. The backend's
    // @validated_request merges request.GET into validator params.
    return bolmateFetch({
        url: URLS.GET.LIST_DEVICE_TOKENS + '?account_id=' + encodeURIComponent(params.account_id),
        callback: params.callback,
        // Forward errorCallback: `callback` fires only on 2xx, so a non-2xx /
        // network failure must reach the caller's errorCallback (else awaited
        // flows hang and loading states never clear). Mirrors renameDeviceToken.
        errorCallback: params.errorCallback
    });
}

export function sendTestPush(params: {account_id: string} & Callback) {
    return bolmateFetch({
        url: URLS.POST.SEND_TEST_PUSH,
        method: FetchMethod.POST,
        data: {account_id: params.account_id},
        callback: params.callback
    });
}

export function sendTestPushToDevice(params: {token_id: string; account_id: string} & Callback) {
    // token_id in the path; account_id in the body. Targets one device only.
    return bolmateFetch({
        url: createParamUrl(URLS.POST.SEND_TEST_PUSH_DEVICE, {token_id: params.token_id}),
        method: FetchMethod.POST,
        data: {account_id: params.account_id},
        callback: params.callback,
        // Forward errorCallback: `callback` fires only on 2xx, so a non-2xx /
        // network failure must reach the caller (the wizard/device list await a
        // Promise resolved from these callbacks — without this an offline/5xx
        // send leaves Promise.all hanging and the loading flag stuck forever).
        errorCallback: params.errorCallback
    });
}

export function sendTestMailToRecipient(params: {email_address: string; account_id: string} & Callback) {
    return bolmateFetch({
        url: URLS.POST.SEND_TEST_MAIL_RECIPIENT,
        method: FetchMethod.POST,
        data: {account_id: params.account_id, email_address: params.email_address},
        callback: params.callback,
        // Forward errorCallback (see sendTestPushToDevice): a failed request
        // must reject the awaited Promise or the caller's loading state hangs.
        errorCallback: params.errorCallback
    });
}

export function updateNotificationChannels(params: UpdateNotificationChannels & Callback) {
    return bolmateFetch({
        url: createParamUrl(URLS.PATCH.UPDATE_NOTIFICATION_CHANNELS, {notification_id: params.notification_id}),
        method: FetchMethod.PATCH,
        data: {
            account_id: params.account_id,
            channels: params.channels,
            // Omitted entirely when undefined — "don't touch targeting" and
            // "clear targeting" ([]) are different instructions to the backend.
            ...(params.push_device_ids !== undefined ? {push_device_ids: params.push_device_ids} : {})
        },
        callback: params.callback,
        // Forward errorCallback: `callback` fires only on 2xx — without this a
        // failed PATCH would leave the channels select frozen (busy) and its
        // optimistic state never rolled back.
        errorCallback: params.errorCallback
    });
}

/*
 ================================================
 OVERVIEW PAGE (/dashboard)
 ================================================
 */
// One endpoint per section of the overview page (overview-openapi v0.0.8).
// While the backend is being built these can be pointed at the Prism mock
// instead — see util/overviewApi.ts for how the target is chosen.
const NO_SUBSCRIPTION_CODE = 'no_subscription';

async function isNoSubscriptionResponse(response: Response): Promise<boolean> {
    try {
        const body = await response.clone().json();
        return body?.code === NO_SUBSCRIPTION_CODE;
    } catch {
        return false;
    }
}

function sendOverviewRequest<T extends OverviewRequest>(url: string, params: WithData<T> & Callback) {
    const usingMock = overviewBaseUrl() !== undefined;
    return bolmateFetch({
        url,
        // undefined for the real backend, so the app-wide platform URL applies.
        baseUrl: overviewBaseUrl(),
        method: FetchMethod.POST,
        // The body IS json, but nothing else in the app declares it — Pyramid
        // parses it either way, while Prism validates against the spec and
        // rejects an undeclared content type with 400.
        headers: {'Content-Type': 'application/json'},
        ...params,
        callback: (data: ResponseData, response: Response) => {
            // The kit derives `success` from the `{status: "success"}` envelope
            // the backend wraps every payload in. The spec does not describe
            // that envelope, so Prism returns the bare schema and every section
            // would read as failed. Trust the HTTP status for the mock instead.
            // (Worth settling in the contract: either the spec documents the
            // envelope, or the endpoints really do answer without one.)
            if (usingMock && response.ok && data.status === undefined) data.success = true;
            params.callback?.(data, response);
        },
        errorCallback: (response: Response) => {
            // The overview contract reports a paused subscription as 403 with a
            // machine-readable code, which never reaches bolmateFetch's own
            // `subscription-failure` gate (that one runs on 2xx bodies). Raise the
            // same paywall here instead of leaving six error boxes on screen.
            if (response?.status === 403) {
                void isNoSubscriptionResponse(response).then(paused => {
                    if (paused) return onTrialEnd();
                    params.errorCallback?.(response);
                });
                return;
            }
            params.errorCallback?.(response);
        }
    });
}

// Top stats are split by cost: general is fast, profit and ads are not, so each
// renders as soon as its own call returns (overview-openapi v0.0.8).
export function getOverviewTopStatsGeneral(params: WithData<OverviewPeriodRequest> & Callback) {
    return sendOverviewRequest(URLS.POST.OVERVIEW_TOP_STATS_GENERAL, params);
}

export function getOverviewTopStatsProfit(params: WithData<OverviewPeriodRequest> & Callback) {
    return sendOverviewRequest(URLS.POST.OVERVIEW_TOP_STATS_PROFIT, params);
}

export function getOverviewTopStatsAds(params: WithData<OverviewPeriodRequest> & Callback) {
    return sendOverviewRequest(URLS.POST.OVERVIEW_TOP_STATS_ADS, params);
}

export function getOverviewProducts(params: WithData<OverviewPeriodRequest> & Callback) {
    return sendOverviewRequest(URLS.POST.OVERVIEW_PRODUCTS, params);
}

export function getOverviewReturns(params: WithData<OverviewPeriodRequest> & Callback) {
    return sendOverviewRequest(URLS.POST.OVERVIEW_RETURNS, params);
}

export function getOverviewReviews(params: WithData<OverviewPeriodRequest> & Callback) {
    return sendOverviewRequest(URLS.POST.OVERVIEW_REVIEWS, params);
}

export function getOverviewBuyBox(params: WithData<OverviewPeriodRequest> & Callback) {
    return sendOverviewRequest(URLS.POST.OVERVIEW_BUY_BOX, params);
}

export function getOverviewStock(params: WithData<OverviewRequest> & Callback) {
    return sendOverviewRequest(URLS.POST.OVERVIEW_STOCK, params);
}

export function getOverviewAdRevenue(params: WithData<OverviewPeriodRequest> & Callback) {
    return sendOverviewRequest(URLS.POST.OVERVIEW_AD_REVENUE, params);
}

/*
 ================================================
 Util
 ================================================
 */

function bolmateFetch(config: RequestOptions) {
    if (requireMaintenanceLogin() && !config.maintenanceAllowed) return null;
    const maintenanceHandler = (isError: boolean) => (response: Response) => {
        let handled: boolean = false;
        // `headers` can be missing two ways: Firefox occasionally omits them, and
        // on a REAL network failure (rejected fetch) dvr-controls passes the
        // TypeError as `response` — which has no `.headers`. In both cases we
        // still must surface the error to the caller's errorCallback, otherwise a
        // Wi-Fi<->LTE switch / offline leaves the login/register spinner hanging
        // forever (the exact case the "check your internet" message is for).
        const headers = response?.headers;
        if (!headers) {
            if (isError) config.errorCallback?.(response);
            return false;
        }
        // Recover from a Cloudflare challenge that hit the PWA after a network
        // switch (Wi-Fi <-> LTE) — auto-reload to re-pass it instead of leaving
        // the app silently dead until the user reopens it. No-op for normal
        // responses. See connectionRecovery.ts.
        handlePossibleConnectionBlock(response);

        const maintenance = headers.get('X-Maintenance') === 'true';
        setMaintenanceMode(maintenance);
        if (maintenance && !getMaintenanceLoggedIn() && !config.maintenanceAllowed) {
            setMaintenanceMode(true);
            navigate('/onderhoud');
            handled = true;
        }
        if (isError) config.errorCallback?.(response);
        return handled;
    };
    const callback = (data: ResponseData, response: Response) => {
        if (maintenanceHandler(false)(response)) return;
        if (data && !data.success && data.message === 'subscription-failure' && data.type !== 'auto_send') {
            // Backend subscription gate. Trigger the "Subscription is paused" paywall. On paywalled
            // pages the dialog blocks the view, so the caller must not surface its own error — mark the
            // response `handled` so controllers checking `data.handled` skip their error path. We still
            // call `config.callback` below so those controllers can clean up (roll back optimistic
            // updates, reset loading state); the old wrapper relied on this contract.
            onTrialEnd();
            data.handled = true;
            // On paywall-exempt pages (settings/admin) there is no dialog to block the view, so the
            // caller's own message is how the user learns access is gated. Clear `handled` for the
            // first failure of a burst (debounced) so exactly one message shows; keep it set for the
            // rest so a dialog isn't stacked per gated request that fails at the same time. Use the
            // route (not the yet-to-mount dialog) to decide, so the first failure isn't mislabelled.
            if (!paywallWillBlock()) {
                const now = Date.now();
                if (now - lastSubscriptionFailureShownAt >= SUBSCRIPTION_FAILURE_DEBOUNCE_MS) {
                    lastSubscriptionFailureShownAt = now;
                    data.handled = false;
                }
            }
        }
        config.callback?.(data, response);
    };
    const csrfToken = getCSRF();
    if (csrfToken) config.headers = {...config.headers, 'X-CSRF-Token': csrfToken};
    config.credentials = 'include';
    return controlFetch({...config, callback, errorCallback: maintenanceHandler(true)});
}
